import { useEffect, useMemo, useRef, useState } from "react";
import { useTasks, useUpdateTask, useCreateTask } from "../hooks/useTasks";
import { useUiStore } from "../state/uiStore";
import { PRIO, STATUS } from "../lib/design";
import {
  dayKey,
  todayKey,
  isoToDayKey,
  dueDateToIso,
  formatDay,
} from "../lib/date";
import { QuickAdd } from "../components/QuickAdd";
import type { TmTask } from "../api/tm";

const DOW = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

/** Calendrier mois/semaine/jours — tâches datées, drag d'une carte = changement
 * d'échéance. « Jours » = colonnes horizontales façon prototype DECO. */
export function CalendarScreen() {
  const tasks = useTasks();
  const updateTask = useUpdateTask();
  const createTask = useCreateTask();
  const { selectedTaskId, selectTask, prefs } = useUiStore();
  const [mode, setMode] = useState<"month" | "week" | "days">("month");
  const [cursor, setCursor] = useState(0);
  const [dragId, setDragId] = useState<string | null>(null);
  const today = todayKey();
  const todayColRef = useRef<HTMLDivElement>(null);

  // Scroll positionné sur aujourd'hui à l'entrée en mode « Jours ».
  useEffect(() => {
    if (mode === "days" && cursor === 0)
      todayColRef.current?.scrollIntoView({
        inline: "start",
        block: "nearest",
      });
  }, [mode, cursor]);

  const byDay = useMemo(() => {
    const m = new Map<string, TmTask[]>();
    for (const t of tasks.data ?? []) {
      if (!t.due_date || t.status === "cancelled") continue;
      const k = isoToDayKey(t.due_date);
      const arr = m.get(k) ?? [];
      arr.push(t);
      m.set(k, arr);
    }
    return m;
  }, [tasks.data]);

  const cells = useMemo(() => {
    const now = new Date();
    if (mode === "month") {
      const base = new Date(now.getFullYear(), now.getMonth() + cursor, 1);
      const offset =
        prefs.weekStart === "mon" ? (base.getDay() + 6) % 7 : base.getDay();
      const start = new Date(base);
      start.setDate(1 - offset);
      return {
        label: base.toLocaleDateString("fr-FR", {
          month: "long",
          year: "numeric",
        }),
        days: Array.from({ length: 42 }, (_, i) => {
          const d = new Date(start);
          d.setDate(start.getDate() + i);
          return {
            key: dayKey(d),
            day: d.getDate(),
            inScope: d.getMonth() === base.getMonth(),
          };
        }),
      };
    }
    if (mode === "days") {
      // Bande de 28 jours, ancrée une semaine avant aujourd'hui.
      const start = new Date(now);
      start.setDate(now.getDate() - 7 + cursor * 7);
      const end = new Date(start);
      end.setDate(start.getDate() + 27);
      return {
        label: `${start.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} – ${end.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}`,
        days: Array.from({ length: 28 }, (_, i) => {
          const d = new Date(start);
          d.setDate(start.getDate() + i);
          return { key: dayKey(d), day: d.getDate(), inScope: true };
        }),
      };
    }
    const monday = new Date(now);
    const dowOffset =
      prefs.weekStart === "mon" ? (now.getDay() + 6) % 7 : now.getDay();
    monday.setDate(now.getDate() - dowOffset + cursor * 7);
    const end = new Date(monday);
    end.setDate(monday.getDate() + 6);
    return {
      label: `${monday.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} – ${end.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}`,
      days: Array.from({ length: 7 }, (_, i) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        return { key: dayKey(d), day: d.getDate(), inScope: true };
      }),
    };
  }, [mode, cursor, prefs.weekStart]);

  const dropOn = (key: string) => {
    if (dragId)
      updateTask.mutate({ id: dragId, body: { due_date: dueDateToIso(key) } });
    setDragId(null);
  };

  const datedCount = [...byDay.values()].reduce((n, a) => n + a.length, 0);

  return (
    <>
      <div
        className="view-header"
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div className="view-title">Calendrier</div>
          <div className="view-caption" style={{ textTransform: "capitalize" }}>
            {cells.label}
          </div>
        </div>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 6,
            alignItems: "center",
          }}
        >
          <button
            className="btn"
            onClick={() => setCursor((c) => c - 1)}
            aria-label="Précédent"
          >
            ‹
          </button>
          <button className="btn" onClick={() => setCursor(0)}>
            Aujourd'hui
          </button>
          <button
            className="btn"
            onClick={() => setCursor((c) => c + 1)}
            aria-label="Suivant"
          >
            ›
          </button>
          <span style={{ width: 10 }} />
          <button
            className="btn"
            style={
              mode === "month"
                ? { background: "var(--bg-active)", fontWeight: 600 }
                : undefined
            }
            onClick={() => {
              setMode("month");
              setCursor(0);
            }}
          >
            Mois
          </button>
          <button
            className="btn"
            style={
              mode === "week"
                ? { background: "var(--bg-active)", fontWeight: 600 }
                : undefined
            }
            onClick={() => {
              setMode("week");
              setCursor(0);
            }}
          >
            Semaine
          </button>
          <button
            className="btn"
            style={
              mode === "days"
                ? { background: "var(--bg-active)", fontWeight: 600 }
                : undefined
            }
            onClick={() => {
              setMode("days");
              setCursor(0);
            }}
          >
            Jours
          </button>
        </div>
      </div>
      <div className="main-scroll" style={{ paddingTop: 16 }}>
        {mode === "days" ? (
          <div className="cal-days">
            {cells.days.map((c) => {
              const isToday = c.key === today;
              const dow = new Date(c.key + "T12:00:00Z").getUTCDay();
              const items = byDay.get(c.key) ?? [];
              return (
                <div
                  key={c.key}
                  ref={isToday ? todayColRef : undefined}
                  className={`cal-day-col${dow === 0 || dow === 6 ? " weekend" : ""}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => dropOn(c.key)}
                >
                  <div className="cal-day-head">
                    {formatDay(c.key)}
                    {isToday && (
                      <span
                        className="chip"
                        style={{ color: "#fff", background: "var(--accent)" }}
                      >
                        aujourd'hui
                      </span>
                    )}
                    <span
                      style={{
                        marginLeft: "auto",
                        fontWeight: 500,
                        color: "var(--ink-ghost)",
                      }}
                    >
                      {items.length || ""}
                    </span>
                  </div>
                  {items.map((t) => (
                    <button
                      key={t.id}
                      className="cal-event"
                      draggable
                      onDragStart={() => setDragId(t.id)}
                      onClick={() =>
                        selectTask(t.id === selectedTaskId ? null : t.id)
                      }
                      title={`${t.title} — ${STATUS[(t.status in STATUS ? t.status : "todo") as keyof typeof STATUS].label}`}
                      style={{
                        color: PRIO[t.priority].color,
                        background: PRIO[t.priority].bg,
                        textDecoration:
                          t.status === "done" ? "line-through" : undefined,
                        outline:
                          t.id === selectedTaskId
                            ? "1.5px solid var(--accent)"
                            : undefined,
                      }}
                    >
                      {t.title}
                    </button>
                  ))}
                  <div className="group-composer">
                    <QuickAdd
                      placeholder="Ajouter…"
                      busy={createTask.isPending}
                      onAdd={(title) =>
                        createTask.mutate({
                          title,
                          priority: prefs.defaultPriority,
                          visibility: prefs.defaultVisibility,
                          due_date: dueDateToIso(c.key),
                        })
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className="cal-grid"
            style={
              mode === "week"
                ? { gridAutoRows: "minmax(220px, 1fr)" }
                : undefined
            }
          >
            {cells.days.slice(0, 7).map((_, i) => (
              <div
                key={`h${i}`}
                style={{
                  padding: "4px 6px",
                  fontSize: 10,
                  fontWeight: 700,
                  color: "var(--ink-ghost)",
                  textTransform: "uppercase",
                  borderRight: "1px solid var(--border-2)",
                }}
              >
                {DOW[prefs.weekStart === "mon" ? i : (i + 6) % 7]}
              </div>
            ))}
            {cells.days.map((c) => (
              <div
                key={c.key}
                className={`cal-cell${c.inScope ? "" : " off"}`}
                style={mode === "week" ? { minHeight: 220 } : undefined}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => dropOn(c.key)}
              >
                <span
                  style={{
                    fontWeight: c.key === today ? 700 : 500,
                    color: c.key === today ? "#fff" : undefined,
                    background: c.key === today ? "var(--accent)" : undefined,
                    borderRadius: 9,
                    padding: c.key === today ? "1px 6px" : undefined,
                  }}
                >
                  {c.day}
                </span>
                {(byDay.get(c.key) ?? []).map((t) => (
                  <button
                    key={t.id}
                    className="cal-event"
                    draggable
                    onDragStart={() => setDragId(t.id)}
                    onClick={() =>
                      selectTask(t.id === selectedTaskId ? null : t.id)
                    }
                    title={`${t.title} — ${STATUS[(t.status in STATUS ? t.status : "todo") as keyof typeof STATUS].label}`}
                    style={{
                      color: PRIO[t.priority].color,
                      background: PRIO[t.priority].bg,
                      textDecoration:
                        t.status === "done" ? "line-through" : undefined,
                      outline:
                        t.id === selectedTaskId
                          ? "1.5px solid var(--accent)"
                          : undefined,
                    }}
                  >
                    {t.title}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
        {datedCount === 0 && !tasks.isLoading && (
          <div className="empty-state">
            Aucune tâche datée sur cette période.
          </div>
        )}
      </div>
    </>
  );
}
